import type Echo from 'laravel-echo'
import { echoLog } from './echoClient'

type EchoClient = Echo<'reverb'>

/**
 * Сколько слушателей держит каждый приватный канал — отдельно на каждый
 * инстанс Echo: после пересоздания клиента старые подписки мертвы, и счёт
 * начинается заново.
 */
const listenersByClient = new WeakMap<EchoClient, Map<string, number>>()

/** Событие pusher-js об успешной подписке — и первой, и повторной после переподключения сокета. */
const SUBSCRIPTION_SUCCEEDED_EVENT = 'pusher:subscription_succeeded'

/**
 * Слушает событие приватного канала, который могут одновременно слушать
 * несколько хуков, и возвращает отписку.
 *
 * Echo слушателей не считает: `echo.private()` отдаёт всем один объект канала,
 * а `echo.leave()` снимает канал целиком — вместе с чужими обработчиками.
 * Канал статуса урока `lesson.{id}.status` слушают и списки уроков, и
 * глобальная плашка текущего урока: без счёта уход со страницы `/lessons`
 * молча отключал бы плашку от событий её урока. Поэтому отписка снимает только
 * свой обработчик, а канал покидается, когда уходит последний слушатель.
 *
 * `onSubscribed` — после каждой успешной подписки канала: первой и повторной,
 * когда pusher-js сам переподписывается после обрыва сокета. События, ушедшие до
 * подписки или за время обрыва, сервер не переигрывает — по этому колбэку
 * слушатель сверяет состояние запросом. Если канал уже подписан другим
 * слушателем, колбэк вызывается сразу: события до привязки обработчика ушли
 * только тому слушателю.
 */
export function listenSharedPrivateChannel<TPayload>(
  echo: EchoClient,
  channelName: string,
  event: string,
  handler: (payload: TPayload) => void,
  onSubscribed?: () => void,
): () => void {
  let counts = listenersByClient.get(echo)
  if (!counts) {
    counts = new Map()
    listenersByClient.set(echo, counts)
  }
  const channelCounts = counts
  channelCounts.set(channelName, (channelCounts.get(channelName) ?? 0) + 1)

  const channel = echo.private(channelName)
  channel.listen(event, handler)
  echoLog(`private-${channelName}: слушателей ${channelCounts.get(channelName)}`)

  // Своя обёртка на каждый вызов: pusher-js снимает привязку по ссылке на функцию,
  // и общий колбэк двух слушателей снялся бы у обоих. Привязка — прямо к подписке:
  // Echo `subscribed()` оборачивает колбэк, и снять его потом нельзя.
  const subscription = channel.subscription
  const handleSubscribed = onSubscribed ? () => onSubscribed() : null
  if (handleSubscribed) {
    subscription.bind(SUBSCRIPTION_SUCCEEDED_EVENT, handleSubscribed)
    if (subscription.subscribed) handleSubscribed()
  }

  let released = false
  return () => {
    if (released) return
    released = true

    try {
      channel.stopListening(event, handler)
      if (handleSubscribed) subscription.unbind(SUBSCRIPTION_SUCCEEDED_EVENT, handleSubscribed)
    } catch {
      // ignore
    }

    const left = (channelCounts.get(channelName) ?? 1) - 1
    if (left > 0) {
      channelCounts.set(channelName, left)
      return
    }

    channelCounts.delete(channelName)
    try {
      echo.leave(channelName)
    } catch {
      // ignore
    }
    echoLog(`private-${channelName}: канал покинут`)
  }
}
